import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Public visitor chat widget — floating gold button bottom-right that opens
 * a chat pane. Powered by Gemini via /api/ai/chat. Session ID is per-tab so
 * conversation stays coherent.
 */
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Bonjour ! Je suis Vlad IA, l'assistant virtuel de VlaDriver VTC. Comment puis-je vous aider ? 🚗",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(
    `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`,
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data?.reply || "…" },
      ]);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? "Trop de messages. Merci de patienter quelques minutes."
          : "Je suis indisponible pour le moment. Appelez le +33 7 83 97 73 70. ☎️";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-widget-toggle"
          aria-label="Ouvrir l'assistant IA"
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#D4AF37] hover:bg-white text-black shadow-xl shadow-black/40 flex items-center justify-center transition-all hover:scale-110 touch-manipulation"
        >
          <MessageCircle size={22} strokeWidth={2} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed inset-x-0 bottom-0 md:inset-x-auto md:bottom-5 md:right-5 z-40 md:w-[380px] md:max-h-[600px] flex flex-col bg-[#0F1115] md:rounded-2xl border-t md:border border-[#D4AF37]/30 shadow-2xl shadow-black/60 backdrop-blur-lg animate-in fade-in slide-in-from-bottom-4"
          style={{ maxHeight: "min(80vh, 600px)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 bg-gradient-to-r from-[#0A0B0D] to-[#151517] md:rounded-t-2xl">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                <MessageCircle size={14} className="text-[#D4AF37]" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm truncate">
                  Vlad IA · Assistant
                </div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                  En ligne
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close"
              aria-label="Fermer"
              className="w-8 h-8 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center touch-manipulation"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-widget-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#D4AF37] text-black rounded-br-md"
                      : "bg-white/8 text-white/90 rounded-bl-md border border-white/10"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/8 border border-white/10 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-white/60 text-sm inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Vlad IA écrit…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/8 bg-[#0A0B0D] md:rounded-b-2xl">
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                data-testid="chat-widget-input"
                rows={1}
                placeholder="Posez votre question…"
                className="flex-1 bg-[#151517] border border-white/10 rounded-2xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#D4AF37] resize-none max-h-24"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !input.trim()}
                data-testid="chat-widget-send"
                aria-label="Envoyer"
                className="w-10 h-10 rounded-full bg-[#D4AF37] hover:bg-white text-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors touch-manipulation shrink-0"
              >
                <Send size={14} strokeWidth={2.4} />
              </button>
            </div>
            <p className="text-[9px] text-white/30 text-center mt-2 tracking-wide">
              Réponses générées par IA · pour une confirmation, appelez +33 7 83 97 73 70
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
